import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Text } from "@pentabd/ui";

import { MultiFileRow, SyncMultiFileRow } from "@components";

import {
  deleteHrDocuments,
  updateHrDocument,
  uploadHrDocuments,
} from "@api/workflow";

import { STATUS } from "@constants/status.hrm";
import { URLS } from "@constants/urls";

import { RetirementType } from "@type/HRM/retirement";
import { UploadFileType } from "@type/HRM/upload-file";

import {
  RetirementFormDataType,
  SUPPORTING_DOCUMENT,
} from "@validations/HRM/retirement/retirementFormValidation";

const { SUCCESS, FAILED } = STATUS;
const { RETIREMENTS, DOCUMENT_UPDATE, DOCUMENTS_UPLOAD, DOCUMENTS_DELETE } =
  URLS;
const { HR_SUPPORTING_DOCUMENT, EMPLOYEE_SUPPORTING_DOCUMENT } =
  SUPPORTING_DOCUMENT;

const hrDocEndpoint = (id: number, path: string) =>
  `${process.env.REACT_APP_RETIREMENT_SERVICE_URL}${process.env.REACT_APP_RETIREMENT_SERVICE_PORT}/${process.env.REACT_APP_RETIREMENT_SERVICE}/api/${process.env.REACT_APP_API_VERSION}/${process.env.REACT_APP_API_PRIVATE}/${RETIREMENTS}/${id}/${path}`;

interface SupportingDocument {
  data: RetirementType;
  isFileUploadDisabled: boolean;
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
}

export const SupportingDocument = ({
  data,
  isFileUploadDisabled,
  setIsSuccessModalOpen,
  setIsErrorModalOpen,
}: SupportingDocument) => {
  const { t } = useTranslation();

  const { id } = data || {};

  const {
    register,
    formState: { errors },
    setValue,
    control,
    watch,
  } = useFormContext<RetirementFormDataType>();

  const handleAddAction = (documents: UploadFileType[]) => {
    uploadHrDocuments(
      hrDocEndpoint(id, DOCUMENTS_UPLOAD),
      documents,
      (response) => {
        if (response.status === SUCCESS) {
          setValue(
            HR_SUPPORTING_DOCUMENT,
            response.data.retirementDemandDetails.retirementDetailsHrDocuments,
          );
        } else if (response.status === FAILED) setIsErrorModalOpen(true);
      },
    );
  };

  const handleDeleteAction = (documentsId: number) => {
    deleteHrDocuments(
      `${hrDocEndpoint(id, DOCUMENTS_DELETE)}/${documentsId}`,
      (response) => {
        if (response.status === FAILED) setIsErrorModalOpen(true);
      },
    );
  };

  const files: any = watch(HR_SUPPORTING_DOCUMENT);
  const updateFile = (selectedFile: UploadFileType) => {
    updateHrDocument(
      `${hrDocEndpoint(id, DOCUMENT_UPDATE)}/${selectedFile.id}`,
      {
        ...selectedFile,
        memorialNo: watch(`memorialNo-${selectedFile.documentId}`),
        documentName: watch(`documentName-${selectedFile.documentId}`),
        date: watch(`date-${selectedFile.documentId}`),
        remarks: watch(`remarks-${selectedFile.documentId}`),
      },
      (response) => {
        if (response.status === SUCCESS) {
          const updatedFiles = files.map((item: UploadFileType) =>
            item.id === response.id ? response : item,
          );
          setValue(HR_SUPPORTING_DOCUMENT, updatedFiles);
          setIsSuccessModalOpen(true);
        } else if (response.status === FAILED) setIsErrorModalOpen(true);
      },
    );
  };

  return (
    <>
      <Text
        weight="semibold"
        size="lg"
        color="dark"
        component="h4"
        className="mb-8 py-4"
      >
        {t("RETIREMENT.FORM.SUPPORTING_DOC.LABEL")}
      </Text>
      <div className="d-flex flex-column gap-8 pb-8 mb-8 border-bottom">
        <MultiFileRow
          label="RETIREMENT.FORM.SUPPORTING_DOC.UPLOAD_DOCUMENT.EMPLOYEE"
          name={EMPLOYEE_SUPPORTING_DOCUMENT}
          control={control}
          setValue={setValue}
          register={register}
          error={errors}
          disabled
          fileDetails={["documentName", "date", "remarks", "memorialNo"]}
        />
        <SyncMultiFileRow
          label="RETIREMENT.FORM.SUPPORTING_DOC.UPLOAD_DOCUMENT.HR"
          name={HR_SUPPORTING_DOCUMENT}
          control={control}
          setValue={setValue}
          register={register}
          error={errors}
          addAction={handleAddAction}
          removeAction={handleDeleteAction}
          disabled={isFileUploadDisabled}
          fileDetails={["documentName", "date", "remarks", "memorialNo"]}
          updateSyncFile={(selectedFile) => updateFile(selectedFile)}
        />
      </div>
    </>
  );
};
